import { useState, type ChangeEvent, type FormEvent } from "react";
import Reveal from "@/components/feature/Reveal";
import { categories } from "@/mocks/categories";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahbb53oh653ivfvnqb0";
const MAX_DETAIL = 500;

type Status = "idle" | "submitting" | "success" | "error";

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function SubmissionForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [detailLength, setDetailLength] = useState(0);

  const handleDetailChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setDetailLength(event.target.value.length);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setDetailLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (!text) return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(String(serverMessage));
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (!isOk) {
        setStatus("error");
        setFormError(
          serverMessage && String(serverMessage).trim()
            ? String(serverMessage)
            : "投稿提交失败，请稍后重试，或改用邮箱私信投稿。",
        );
        return;
      }

      setStatus("success");
      form.reset();
      setDetailLength(0);
    } catch {
      setStatus("error");
      setFormError("网络异常，投稿未送达，请稍后重试，或改用邮箱私信投稿。");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

  return (
    <section id="submit" className="relative w-full overflow-hidden bg-foreground-950 py-16 md:py-24">
      <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-primary-500/20 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-16 bottom-0 h-64 w-64 rounded-full bg-accent-500/10 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-6xl px-4 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
          <Reveal>
            <div>
              <p className="mb-3 flex items-center gap-2 text-xs font-label font-medium uppercase tracking-[0.28em] text-accent-400">
                <span className="h-px w-6 bg-accent-500" />
                在线投稿入口
              </p>
              <h2 className="font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
                把你知道的，写在这里
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-background-200 md:text-base">
                带星号的项目请尽量填写完整，细节越具体，编辑越容易判断线索的价值。
                你不需要提供真实身份，也不需要一次说完——先把最重要的部分留下就好。
              </p>

              <ul className="mt-8 space-y-4">
                {[
                  { icon: "ri-eye-off-line", text: "默认匿名，无需注册与实名" },
                  { icon: "ri-time-line", text: "1 至 3 个工作日内完成初筛" },
                  { icon: "ri-delete-bin-6-line", text: "发布前可随时要求撤回并删除" },
                  { icon: "ri-mail-send-line", text: "联系方式选填，仅用于核实线索" },
                ].map((item) => (
                  <li key={item.text} className="flex items-center gap-3 text-sm text-background-100">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50/10 text-lg text-accent-400">
                      <i className={item.icon} aria-hidden="true" />
                    </span>
                    {item.text}
                  </li>
                ))}
              </ul>

              <p className="mt-8 rounded-md border border-background-50/15 bg-background-50/5 p-4 text-xs leading-relaxed text-background-200">
                <i className="ri-information-line mr-1 text-accent-400" aria-hidden="true" />
                请勿提交涉及他人隐私的原始证件、住址等敏感材料；如确有需要，编辑会另行告知安全的提交方式。
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="rounded-lg bg-background-50 p-6 md:p-8">
              <form data-readdy-form id="guatian-submit-form" onSubmit={handleSubmit} noValidate>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot-field"
                />

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="tip-category" className="mb-2 block text-sm font-medium text-foreground-800">
                      线索分类 <span className="text-primary-600">*</span>
                    </label>
                    <select
                      id="tip-category"
                      name="category"
                      required
                      defaultValue=""
                      className={`${inputClass} cursor-pointer`}
                    >
                      <option value="" disabled>
                        请选择一个分类
                      </option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="tip-contact" className="mb-2 block text-sm font-medium text-foreground-800">
                      联系方式<span className="ml-1 text-xs font-normal text-foreground-500">（选填）</span>
                    </label>
                    <input
                      id="tip-contact"
                      name="contact"
                      type="text"
                      autoComplete="off"
                      placeholder="邮箱、社交账号或其他可联系到你的方式"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="tip-title" className="mb-2 block text-sm font-medium text-foreground-800">
                    线索标题 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="tip-title"
                    name="title"
                    type="text"
                    required
                    maxLength={80}
                    autoComplete="off"
                    placeholder="用一句话概括你要说的线索"
                    className={inputClass}
                  />
                </div>

                <div className="mt-5">
                  <div className="mb-2 flex items-end justify-between">
                    <label htmlFor="tip-detail" className="block text-sm font-medium text-foreground-800">
                      详细内容 <span className="text-primary-600">*</span>
                    </label>
                    <span className="text-xs text-foreground-500">
                      {detailLength}/{MAX_DETAIL}
                    </span>
                  </div>
                  <textarea
                    id="tip-detail"
                    name="detail"
                    required
                    rows={6}
                    maxLength={MAX_DETAIL}
                    onChange={handleDetailChange}
                    placeholder="尽量交代时间、地点、涉及方与具体经过，例如：今年 5 月，某门店……"
                    className={`${inputClass} resize-none leading-relaxed`}
                  />
                </div>

                {status === "success" ? (
                  <p
                    role="status"
                    className="mt-5 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900"
                  >
                    <i className="ri-checkbox-circle-line mt-0.5 text-base" aria-hidden="true" />
                    投稿已送达，感谢你提供的线索。编辑团队会尽快查看，如需进一步核实会主动联系你。
                  </p>
                ) : null}

                {status === "error" ? (
                  <p
                    role="alert"
                    className="mt-5 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800"
                  >
                    <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true" />
                    {formError}
                  </p>
                ) : null}

                <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-foreground-500">
                    提交即表示你同意编辑在隐去身份信息后核实并采用该线索。
                  </p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-background-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"} aria-hidden="true" />
                    {status === "submitting" ? "正在提交…" : "提交投稿"}
                  </button>
                </div>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}